// Analytics API Service for React Frontend
// File: react-front/src/services/analyticsService.js

import { API_BASE_URL } from '../config';
import authFetch from '../utils/authFetch';

const API_URL = `${API_BASE_URL}/api`;

class AnalyticsService {
  /**
   * Fetch KPI data for dashboard cards
   */
  async getKpis() {
    try {
      const response = await authFetch(`${API_URL}/analytics/kpis`);
      if (!response.ok) {
        throw new Error('Failed to fetch KPI data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      throw error;
    }
  }

  /**
   * Fetch population trends for charts
   */
  async getPopulationTrends(period = '7d') {
    try {
      const response = await authFetch(`${API_URL}/analytics/population-trends?period=${period}`);
      if (!response.ok) {
        throw new Error('Failed to fetch population trends');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching population trends:', error);
      throw error;
    }
  }

  /**
   * Fetch demographics data (age groups and gender distribution)
   */
  async getDemographics() {
    try {
      const response = await authFetch(`${API_URL}/analytics/demographics`);
      if (!response.ok) {
        throw new Error('Failed to fetch demographics data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching demographics:', error);
      throw error;
    }
  }

  /**
   * Fetch age distribution data specifically
   */
  async getAgeDistribution() {
    try {
      const response = await authFetch(`${API_URL}/analytics/age-distribution`);
      if (!response.ok) {
        throw new Error('Failed to fetch age distribution data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching age distribution:', error);
      throw error;
    }
  }

  /**
   * Fetch gender distribution data specifically
   */
  async getGenderDistribution() {
    try {
      const response = await authFetch(`${API_URL}/analytics/gender-distribution`);
      if (!response.ok) {
        throw new Error('Failed to fetch gender distribution data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching gender distribution:', error);
      throw error;
    }
  }

  /**
   * Fetch recent system activities
   */
  async getSystemActivities() {
    try {
      const response = await authFetch(`${API_URL}/analytics/activities`);
      if (!response.ok) {
        throw new Error('Failed to fetch system activities');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching system activities:', error);
      throw error;
    }
  }

  /**
   * Fetch facility analytics (capacity, blocks)
   */
  async getFacilityAnalytics() {
    try {
      const response = await authFetch(`${API_URL}/analytics/facility`);
      if (!response.ok) {
        throw new Error('Failed to fetch facility analytics');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching facility analytics:', error);
      throw error;
    }
  }

  /**
   * Fetch all dashboard data in one request
   */
  async getDashboardData() {
    try {
      const response = await authFetch(`${API_URL}/analytics/dashboard`);
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed health records count from hr_info table
   */
  async getHealthRecordsCount() {
    try {
      const response = await authFetch(`${API_URL}/analytics/health-records-count`);
      if (!response.ok) {
        throw new Error('Failed to fetch health records count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching health records count:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed general dormitory count from hr_c_tb_conclusion table
   */
  async getGeneralDormitoryCount() {
    try {
      const response = await authFetch(`${API_URL}/analytics/general-dormitory-count`);
      if (!response.ok) {
        throw new Error('Failed to fetch general dormitory count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching general dormitory count:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed isolation count from hr_c_tb_conclusion table
   */
  async getIsolationCount() {
    try {
      const response = await authFetch(`${API_URL}/analytics/isolation-count`);
      if (!response.ok) {
        throw new Error('Failed to fetch isolation count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching isolation count:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed hospital count from hr_c_tb_conclusion table
   */
  async getHospitalCount() {
    try {
      const response = await authFetch(`${API_URL}/analytics/hospital-count`);
      if (!response.ok) {
        throw new Error('Failed to fetch hospital count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching hospital count:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed daily consultation count from health_consult table
   */
  async getDailyConsultationCount() {
    try {
      const response = await authFetch(`${API_URL}/analytics/daily-consultation-count`);
      if (!response.ok) {
        throw new Error('Failed to fetch daily consultation count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching daily consultation count:', error);
      throw error;
    }
  }

  /**
   * Fetch count of unassigned PDLs (hr_info rows with NULL cell_id)
   */
  async getUnassignedPdlCount() {
    try {
      const response = await authFetch(`${API_URL}/analytics/unassigned-pdls-count`);
      if (!response.ok) {
        throw new Error('Failed to fetch unassigned PDLs count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching unassigned PDLs count:', error);
      throw error;
    }
  }

  /**
   * Fetch cell distribution data showing all cells and their inmate counts
   */
  async getCellDistribution() {
    try {
      const response = await authFetch(`${API_URL}/analytics/cell-distribution`);
      if (!response.ok) {
        throw new Error('Failed to fetch cell distribution');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cell distribution:', error);
      throw error;
    }
  }
}

// Create and export instance
const analyticsService = new AnalyticsService();
export default analyticsService;

// Example usage in React component:
/*
import React, { useState, useEffect } from 'react';
import analyticsService from '../services/analyticsService';
import authFetch from '../utils/authFetch';
function Dashboard() {
  const [kpiData, setKpiData] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [activities, setActivities] = useState(null);
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Option 1: Load all data at once
      const dashboardData = await analyticsService.getDashboardData();
      setKpiData(dashboardData.kpis);
      setDemographics(dashboardData.demographics);
      setActivities(dashboardData.activities);
      setFacility(dashboardData.facility);
      
      // Option 2: Load data separately (for better loading states)
      // const [kpis, demo, acts, fac] = await Promise.all([
      //   analyticsService.getKpis(),
      //   analyticsService.getDemographics(),
      //   analyticsService.getSystemActivities(),
      //   analyticsService.getFacilityAnalytics()
      // ]);
      // setKpiData(kpis);
      // setDemographics(demo);
      // setActivities(acts);
      // setFacility(fac);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <KpiCards data={kpiData} />
      <DemographicsChart data={demographics} />
      <ActivityTimeline data={activities} />
      <FacilityStatus data={facility} />
    </div>
  );
}
*/